import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'

export default function ProjectQA({ projectId, isDeveloper = false }) {
  const queryClient = useQueryClient()
  const { user, profile } = useAuth()
  const [showAskForm, setShowAskForm] = useState(false)
  const [question, setQuestion] = useState('')
  const [answeringId, setAnsweringId] = useState(null)
  const [answerText, setAnswerText] = useState('')
  const [error, setError] = useState(null)
  const [showAll, setShowAll] = useState(false)

  const { data: questionsData, isLoading } = useQuery({
    queryKey: ['project-questions', projectId, isDeveloper],
    queryFn: async () => {
      let query = supabase
        .from('project_questions')
        .select(`
          *,
          asker:asker_id (
            id,
            full_name
          ),
          answerer:answered_by (
            id,
            full_name
          )
        `)
        .eq('project_id', projectId)
        .order('net_votes', { ascending: false })
        .order('created_at', { ascending: false })

      if (!isDeveloper) {
        query = query.eq('is_public', true)
      }

      const { data, error } = await query

      if (error) throw error
      return data
    },
  })

  const { data: userVotesData } = useQuery({
    queryKey: ['user-question-votes', projectId],
    queryFn: async () => {
      if (!user) return []

      const { data, error } = await supabase
        .from('question_votes')
        .select('question_id, vote_type')
        .in('question_id', questionsData?.map(q => q.id) || [])
        .eq('user_id', user.id)

      if (error) throw error
      return data || []
    },
    enabled: !!user && !!questionsData?.length,
  })

  const askMutation = useMutation({
    mutationFn: async (questionText) => {
      const { data, error } = await supabase
        .from('project_questions')
        .insert({
          project_id: projectId,
          asker_id: user?.id || null,
          asker_name: profile?.full_name || 'Anonymous',
          question: questionText,
          is_public: true,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project-questions', projectId])
      setShowAskForm(false)
      setQuestion('')
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to submit question')
    },
  })

  const answerMutation = useMutation({
    mutationFn: async ({ questionId, answer }) => {
      const { data, error } = await supabase
        .from('project_questions')
        .update({
          answer,
          answered_by: user.id,
          answered_at: new Date().toISOString(),
        })
        .eq('id', questionId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project-questions', projectId])
      setAnsweringId(null)
      setAnswerText('')
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to submit answer')
    },
  })

  const togglePublicMutation = useMutation({
    mutationFn: async ({ questionId, isPublic }) => {
      const { error } = await supabase
        .from('project_questions')
        .update({ is_public: isPublic })
        .eq('id', questionId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project-questions', projectId])
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to update visibility')
    },
  })

  const voteMutation = useMutation({
    mutationFn: async ({ questionId, voteType }) => {
      const { data, error } = await supabase.rpc('vote_on_question', {
        p_question_id: questionId,
        p_vote_type: voteType,
      })

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project-questions', projectId])
      queryClient.invalidateQueries(['user-question-votes', projectId])
    },
    onError: (err) => {
      setError(err.message || 'Failed to register vote')
      setTimeout(() => setError(null), 3000)
    },
  })

  const handleAskSubmit = (e) => {
    e.preventDefault()
    if (!question.trim()) return
    askMutation.mutate(question.trim())
  }

  const handleAnswerSubmit = (e, questionId) => {
    e.preventDefault()
    if (!answerText.trim()) return
    answerMutation.mutate({ questionId, answer: answerText.trim() })
  }

  const handleVote = (questionId, voteType) => {
    if (!user) {
      setError('Please sign in to vote')
      setTimeout(() => setError(null), 3000)
      return
    }
    voteMutation.mutate({ questionId, voteType })
  }

  const getUserVote = (questionId) => {
    return userVotesData?.find(v => v.question_id === questionId)?.vote_type || null
  }

  const questions = questionsData || []
  const unansweredCount = questions.filter(q => !q.answer).length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  const displayedQuestions = showAll || isDeveloper ? questions : questions.slice(0, 1)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">
            <span className="hidden sm:inline">Questions & Answers</span>
            <span className="sm:hidden">Q&A</span>
          </h3>
          <p className="text-sm text-secondary-600">
            {isDeveloper
              ? `${unansweredCount} unanswered questions`
              : 'Ask questions about this project'}
          </p>
        </div>
        {!isDeveloper && (
          <button
            onClick={() => setShowAskForm(!showAskForm)}
            className="btn-primary px-3 py-2 text-sm"
          >
            {showAskForm ? 'Cancel' : 'Ask'}
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {showAskForm && (
        <form onSubmit={handleAskSubmit} className="card p-4 sm:p-6 bg-secondary-50">
          <label className="label">Your Question</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="input min-h-[100px]"
            placeholder="What would you like to know about this project?"
            required
          />
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => setShowAskForm(false)}
              className="btn-outline flex-1 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={askMutation.isPending}
              className="btn-primary flex-1 py-2"
            >
              {askMutation.isPending ? 'Submitting...' : 'Submit Question'}
            </button>
          </div>
        </form>
      )}

      {questions.length === 0 ? (
        <div className="card p-6 sm:p-8 text-center">
          <div className="text-secondary-400 mb-3">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-secondary-900 mb-1">No questions yet</h4>
          <p className="text-secondary-600">
            {isDeveloper ? 'Questions from buyers will appear here' : 'Be the first to ask a question'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayedQuestions.map((q) => {
            const userVote = getUserVote(q.id)
            return (
              <div key={q.id} className="card p-4 sm:p-6">
                <div className="flex gap-4">
                  {/* Voting Section */}
                  <div className="flex flex-col items-center gap-1 min-w-[48px]">
                    <button
                      onClick={() => handleVote(q.id, 'up')}
                      disabled={voteMutation.isPending}
                      className={`p-1 rounded hover:bg-secondary-100 transition-colors ${
                        userVote === 'up' ? 'text-primary-600' : 'text-secondary-400'
                      }`}
                      title="Upvote"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <div className="text-sm font-semibold text-secondary-900 min-w-[24px] text-center">
                      {q.net_votes || 0}
                    </div>
                    <button
                      onClick={() => handleVote(q.id, 'down')}
                      disabled={voteMutation.isPending}
                      className={`p-1 rounded hover:bg-secondary-100 transition-colors ${
                        userVote === 'down' ? 'text-red-600' : 'text-secondary-400'
                      }`}
                      title="Downvote"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  {/* Question Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-secondary-900">
                            {q.asker?.full_name || q.asker_name}
                          </span>
                          <span className="text-xs text-secondary-500">
                            {new Date(q.created_at).toLocaleDateString('en-NZ', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          {isDeveloper && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                q.is_public
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {q.is_public ? 'Public' : 'Private'}
                            </span>
                          )}
                        </div>
                        <p className="text-secondary-900">{q.question}</p>
                      </div>
                      {isDeveloper && (
                        <button
                          onClick={() =>
                            togglePublicMutation.mutate({
                              questionId: q.id,
                              isPublic: !q.is_public,
                            })
                          }
                          className="text-xs text-primary-600 hover:text-primary-700 ml-4"
                          disabled={togglePublicMutation.isPending}
                        >
                          {q.is_public ? 'Make Private' : 'Make Public'}
                        </button>
                      )}
                    </div>

                    {q.answer ? (
                      <div className="bg-primary-50 border-l-4 border-primary-600 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-primary-900">
                            {q.answerer?.full_name || 'Developer'}
                          </span>
                          <span className="text-xs text-primary-700">
                            {new Date(q.answered_at).toLocaleDateString('en-NZ', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        </div>
                        <p className="text-primary-900 whitespace-pre-wrap">{q.answer}</p>
                      </div>
                    ) : isDeveloper ? (
                      answeringId === q.id ? (
                        <form onSubmit={(e) => handleAnswerSubmit(e, q.id)}>
                          <textarea
                            value={answerText}
                            onChange={(e) => setAnswerText(e.target.value)}
                            className="input min-h-[100px] mb-3"
                            placeholder="Type your answer..."
                            required
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setAnsweringId(null)
                                setAnswerText('')
                              }}
                              className="btn-outline text-sm py-1.5 px-3"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={answerMutation.isPending}
                              className="btn-primary text-sm py-1.5 px-3"
                            >
                              {answerMutation.isPending ? 'Submitting...' : 'Submit Answer'}
                            </button>
                          </div>
                        </form>
                      ) : (
                        <button
                          onClick={() => setAnsweringId(q.id)}
                          className="btn-outline text-sm py-2 px-4 mt-2"
                        >
                          Answer Question
                        </button>
                      )
                    ) : (
                      <div className="text-sm text-secondary-500 italic mt-2">
                        Awaiting response from developer
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          {!isDeveloper && questions.length > 1 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full py-3 text-center text-primary-600 hover:text-primary-700 font-medium hover:bg-primary-50 rounded-lg transition-colors"
            >
              Show {questions.length - 1} More {questions.length - 1 === 1 ? 'Question' : 'Questions'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
